import { useState, useMemo } from "react";
import { Box, Typography, CircularProgress, Alert, LinearProgress } from "@mui/material";
import { CheckCircle, Lock } from "@mui/icons-material";
import { useForm, SubmitHandler } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import {
  useValidateResetTokenQuery,
  useConfirmPasswordResetMutation,
} from "features/auth/auth";
import CustomPasswordField from "./common/CustomPasswordField";
import CustomButton from "./common/CustomButton";

type ResetPasswordInputs = {
  new_password: string;
  confirm_password: string;
};

const passwordRequirements = [
  { label: "At least 8 characters long", test: (p: string) => p.length >= 8 },
  { label: "One uppercase letter", test: (p: string) => /[A-Z]/.test(p) },
  { label: "One number", test: (p: string) => /[0-9]/.test(p) },
  { label: "One symbol", test: (p: string) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(p) },
];

export default function ResetPasswordWithToken() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [resetSuccess, setResetSuccess] = useState(false);

  const { data: tokenData, isLoading: isValidating, error: tokenError } =
    useValidateResetTokenQuery(token || "", { skip: !token });

  const [confirmReset, { isLoading: isResetting }] =
    useConfirmPasswordResetMutation();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordInputs>();

  const watchPassword = watch("new_password", "");

  const passwordStrength = useMemo(() => {
    const passed = passwordRequirements.filter((req) => req.test(watchPassword)).length;
    return (passed / passwordRequirements.length) * 100;
  }, [watchPassword]);

  const getStrengthLabel = () => {
    if (passwordStrength <= 25) return { label: "Weak", color: "#f44336" };
    if (passwordStrength <= 50) return { label: "Fair", color: "#ff9800" };
    if (passwordStrength <= 75) return { label: "Good", color: "#2196f3" };
    return { label: "Strong", color: "#4caf50" };
  };

  const strengthInfo = getStrengthLabel();

  const onSubmit: SubmitHandler<ResetPasswordInputs> = async (data) => {
    if (!token) return;
    setServerError(null);
    try {
      await confirmReset({
        token,
        new_password: data.new_password,
      }).unwrap();
      setResetSuccess(true);
    } catch (error: any) {
      if (error?.data?.error?.new_password) {
        const msg = error.data.error.new_password;
        setServerError(Array.isArray(msg) ? msg[0] : msg);
      } else {
        setServerError("Failed to reset password. Please try again or request a new link.");
      }
    }
  };

  if (isValidating) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (tokenError || !tokenData?.valid) {
    return (
      <Box
        marginTop={12}
        display="flex"
        flexDirection="column"
        alignItems="center"
        className="rounded-lg bg-white p-6 text-gray-800"
      >
        <Typography variant="h5" color="error" gutterBottom>
          Invalid or Expired Link
        </Typography>
        <Typography color="text.secondary" textAlign="center" mb={3}>
          This password reset link is invalid or has expired. Please request a new one.
        </Typography>
        <CustomButton onClick={() => navigate("/login")} fullWidth={false}>
          Back to Login
        </CustomButton>
      </Box>
    );
  }

  if (resetSuccess) {
    return (
      <Box
        marginTop={12}
        display="flex"
        flexDirection="column"
        alignItems="center"
        className="rounded-lg bg-white p-6 text-gray-800"
      >
        <CheckCircle sx={{ fontSize: 60, color: "rgb(31, 93, 107)", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Password Successfully Reset
        </Typography>
        <Typography color="text.secondary" textAlign="center" mb={3}>
          Your password has been successfully reset. You can now log in with your new password.
        </Typography>
        <CustomButton onClick={() => navigate("/login")} fullWidth={false}>
          Go to Login
        </CustomButton>
      </Box>
    );
  }

  return (
    <Box
      marginTop={12}
      display="flex"
      flexDirection="column"
      alignItems="center"
      className="rounded-lg bg-white p-6 text-gray-800"
    >
      <Typography
        component="h1"
        variant="h4"
        sx={{ fontSize: "32px" }}
        className="mb-4 text-[rgb(31,93,107)]"
      >
        Reset Password
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ width: "100%", maxWidth: 400 }}
      >
        <CustomPasswordField
          label="New Password"
          id="new_password"
          autoComplete="new-password"
          error={Boolean(errors.new_password)}
          helperText={errors.new_password?.message}
          InputProps={{
            startAdornment: <Lock sx={{ color: "gray", mr: 1 }} />,
          }}
          {...register("new_password", {
            required: "Password is required",
            minLength: {
              value: 8,
              message: "Password must be at least 8 characters",
            },
            validate: {
              hasUppercase: (v) => /[A-Z]/.test(v) || "Must contain uppercase letter",
              hasNumber: (v) => /[0-9]/.test(v) || "Must contain a number",
              hasSymbol: (v) =>
                /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(v) || "Must contain a symbol",
            },
          })}
        />

        {watchPassword && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={passwordStrength}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "#e0e0e0",
                "& .MuiLinearProgress-bar": {
                  bgcolor: strengthInfo.color,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: strengthInfo.color, fontWeight: "bold", mt: 0.5 }}
            >
              {strengthInfo.label}
            </Typography>

            <Box sx={{ mt: 1 }}>
              {passwordRequirements.map((req, idx) => (
                <Typography
                  key={idx}
                  variant="caption"
                  display="flex"
                  alignItems="center"
                  sx={{
                    color: req.test(watchPassword) ? "#4caf50" : "text.secondary",
                  }}
                >
                  {req.test(watchPassword) ? "✓" : "○"} {req.label}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        <CustomPasswordField
          label="Confirm New Password"
          id="confirm_password"
          autoComplete="new-password"
          error={Boolean(errors.confirm_password)}
          helperText={errors.confirm_password?.message}
          InputProps={{
            startAdornment: <Lock sx={{ color: "gray", mr: 1 }} />,
          }}
          {...register("confirm_password", {
            required: "Please confirm your password",
            validate: (value) =>
              value === watchPassword || "Passwords do not match",
          })}
        />

        {serverError && (
          <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
            {serverError}
          </Alert>
        )}

        {isResetting ? (
          <Box display="flex" justifyContent="center" mt={3}>
            <CircularProgress />
          </Box>
        ) : (
          <CustomButton type="submit" sx={{ mt: 3 }}>
            UPDATE PASSWORD
          </CustomButton>
        )}
      </Box>
    </Box>
  );
}
